<template>
    <div>
      <Header />
  
      <!-- Page Header -->
      <div class="page-header">
        <div>
          <h5 class="page-title">Payslip History</h5>
          <p class="page-sub">Complete record of all employee payslips across all periods</p>
        </div>
        <div class="page-actions">
  
          <div class="search-wrap">
            <font-awesome-icon icon="search" class="search-icon" />
            <input type="text" class="search-input" placeholder="Search payslips..." @input="tableData.page = 1; fetchData()" v-model="tableData.search" />
          </div>
  
          <button class="btn-export-sm" @click="openExportModal('csv')" :disabled="exportLoading"><font-awesome-icon icon="file-csv" /> CSV</button>
          <button class="btn-export-sm btn-export-sm--pdf" @click="openExportModal('pdf')" :disabled="exportLoading"><font-awesome-icon icon="file-pdf" /> PDF</button>
  
        </div>
      </div>
  
      <!-- Data Card -->
      <div class="data-card">
  
        <!-- Loading -->
        <div v-if="loading" class="state-block">
          <div class="spinner-border text-primary" style="width:3rem;height:3rem;" role="status"></div>
          <p class="mt-3 text-muted-cell">Loading payslips...</p>
        </div>
  
        <!-- 403 -->
        <div v-else-if="permission_status == 403" class="state-block">
          <div class="state-icon state-icon--red"><font-awesome-icon icon="lock" /></div>
          <p class="state-title">Access Denied</p>
          <p class="state-sub">You don't have permission to view this resource.</p>
        </div>
  
        <!-- Empty -->
        <div v-else-if="details.length === 0" class="state-block">
          <div class="state-icon state-icon--blue"><font-awesome-icon icon="file-invoice-dollar" /></div>
          <p class="state-title">No payslips found</p>
          <p class="state-sub">Payslips will appear here once payroll periods are processed.</p>
        </div>
  
        <!-- Table -->
        <template v-else>
  
          <div class="table-toolbar">
            <span class="table-count">
              Showing <strong>{{ pagination.from }}–{{ pagination.to }}</strong> of <strong>{{ pagination.total }}</strong>
            </span>
            <div class="toolbar-right">
              <label class="table-count">Rows:</label>
              <select v-model="tableData.length" @change="tableData.page = 1; fetchData()" class="rows-select">
                <option :value="5">5 / page</option>
                <option :value="10">10 / page</option>
                <option :value="20">20 / page</option>
                <option :value="50">50 / page</option>
                <option :value="100">100 / page</option>
                <option :value="200">200 / page</option>
                <option :value="500">500 / page</option>
                <option :value="1000">1000 / page</option>
              </select>
            </div>
          </div>
  
          <div class="table-wrap">
            <table class="data-table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Employee</th>
                  <th>Period</th>
                  <th class="text-right">Basic</th>
                  <th class="text-right">Gross</th>
                  <th class="text-right">Tax</th>
                  <th class="text-right">Pension</th>
                  <th class="text-right">Deductions</th>
                  <th class="text-right">Earnings</th>
                  <th class="text-right">Net</th>
                  <th>Status</th>
                  <th class="col-actions">View</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(row, index) in details" :key="index">
  
                  <td class="text-muted-cell">{{ index + 1 }}</td>
  
                  <td>
                    <div class="employee-cell">
                      <div class="employee-avatar">{{ initials(row.employee) }}</div>
                      <div>
                        <p class="employee-name">{{ row.employee?.first_name }} {{ row.employee?.last_name }}</p>
                        <p class="employee-sub">{{ row.employee?.employee_no }}</p>
                      </div>
                    </div>
                  </td>
  
                  <td>
                    <p class="period-name">{{ row.period?.name }}</p>
                    <span class="batch-code">{{ row.period?.reference }}</span>
                  </td>
  
                  <td class="text-right text-muted-cell">{{ formatPrice(row.basic) }}</td>
                  <td class="text-right fw-bold">{{ formatPrice(row.gross) }}</td>
                  <td class="text-right text-muted-cell">{{ formatPrice(row.tax) }}</td>
                  <td class="text-right text-muted-cell">{{ formatPrice(row.pension) }}</td>
                  <td class="text-right deduction-value">{{ formatPrice(row.additional_deduction) }}</td>
                  <td class="text-right earning-value">{{ formatPrice(row.additional_earning) }}</td>
                  <td class="text-right fw-bold amount-value">{{ formatPrice(row.net) }}</td>
  
                  <td>
                    <span class="status-badge" :class="statusClass(row?.period?.status)">
                      {{ capitalize(row?.period?.status) }}
                    </span>
                  </td>
  
                  <td class="col-actions">
                    <router-link
                      :to="permission.process_payroll == 1 ? '/payslip/' + row.id : '#'"
                      class="action-btn action-btn--view"
                      :class="permission.process_payroll != 1 ? 'disabled' : ''"
                      title="View payslip"
                    >
                      <font-awesome-icon icon="eye" />
                    </router-link>
                  </td>
  
                </tr>
              </tbody>
            </table>
          </div>
  
          <div class="table-footer">
            <pagination :pagination="pagination" @prev="tableData.page = pagination.currentPage - 1; fetchData()" @next="tableData.page = pagination.currentPage + 1; fetchData()" />
          </div>
  
        </template>
      </div>

    </div>

    <!-- ── Export Column Picker ── -->
    <transition name="fade">
      <div v-if="showExportModal" class="col-picker-backdrop" @click.self="showExportModal = false">
        <div class="col-picker-modal">
          <div class="col-picker-header">
            <div>
              <h3 class="col-picker-title">Select Columns</h3>
              <p class="col-picker-sub">Choose what to include in your {{ pendingExportFmt.toUpperCase() }} export</p>
            </div>
            <button class="col-picker-close" @click="showExportModal = false"><font-awesome-icon icon="times" /></button>
          </div>
          <div class="col-picker-actions">
            <button class="col-picker-link" @click="selectedExportCols = exportColumnDefs.map(c => c.key)">Select all</button>
            <span class="col-picker-sep">·</span>
            <button class="col-picker-link" @click="selectedExportCols = []">Deselect all</button>
            <span class="col-picker-count">{{ selectedExportCols.length }} of {{ exportColumnDefs.length }} selected</span>
          </div>
          <div class="col-picker-grid">
            <label v-for="col in exportColumnDefs" :key="col.key" class="col-picker-item">
              <input type="checkbox" :value="col.key" v-model="selectedExportCols" class="col-picker-check" />
              <span class="col-picker-label">{{ col.label }}</span>
            </label>
          </div>
          <div class="col-picker-footer">
            <button class="col-picker-btn-ghost" @click="showExportModal = false">Cancel</button>
            <button class="col-picker-btn-primary" :disabled="selectedExportCols.length === 0 || exportLoading" @click="doExport()">
              <font-awesome-icon :icon="exportLoading ? 'spinner' : pendingExportFmt === 'pdf' ? 'file-pdf' : 'file-csv'" :spin="exportLoading" />
              Export {{ pendingExportFmt.toUpperCase() }}
            </button>
          </div>
        </div>
      </div>
    </transition>

  </template>

  <script>
  import { reusableMixin } from '../../../../lib/reusableMixin';
  import Header from './component/header.vue';
  import axios from 'axios';
  import { useToast } from 'vue-toastification';
  import { useStore } from 'vuex';
  import moment from 'moment';
  import jsPDF from 'jspdf';
  import 'jspdf-autotable';
  
  export default {
    components: { Header },
    mixins: [reusableMixin],
  
    data() {
      return {
        print_content:    [],
        headerArrayColumn: ['S/N','Reference','Period Name','Employee','Basic Salary','Gross Amount','Net Amount','Tax Amount','Pension Amount','Status'],
        details:          [],
        loading:          false,
        permission_status: null,
        exportLoading:    false,
        showExportModal:  false,
        pendingExportFmt: 'csv',
        selectedExportCols: [],
        exportColumnDefs: [
          { key: 'employee',   label: 'Employee',   pdfWidth: 100 },
          { key: 'period',     label: 'Period',     pdfWidth: 100 },
          { key: 'basic',      label: 'Basic',      pdfWidth: 72 },
          { key: 'gross',      label: 'Gross',      pdfWidth: 72 },
          { key: 'tax',        label: 'Tax',        pdfWidth: 65 },
          { key: 'pension',    label: 'Pension',    pdfWidth: 68 },
          { key: 'deductions', label: 'Deductions', pdfWidth: 80 },
          { key: 'earnings',   label: 'Earnings',   pdfWidth: 80 },
          { key: 'net',        label: 'Net',        pdfWidth: 78 },
          { key: 'status',     label: 'Status',     pdfWidth: 68 },
        ],

        tableData: { page: 1, draw:0, length:5, search:'', column:0, dir:'desc', id:'' },
        pagination: { lastPage:'', currentPage:'', total:'', lastPageUrl:'', nextPageUrl:'', prevPageUrl:'', from:'', to:'' },
        toast:  useToast(),
        store:  useStore(),
      };
    },
  
    computed: {
      permission() { return this.store.state.getCurrentUserPermission; },
    },
  
    created() { this.fetchData(); },
  
    methods: {
      initials(employee) {
        if (!employee) return '?';
        return ((employee.first_name?.[0] ?? '') + (employee.last_name?.[0] ?? '')).toUpperCase();
      },
  
      capitalize(s) { return s ? s.charAt(0).toUpperCase() + s.slice(1) : '—'; },
  
      statusClass(status) {
        return { open:'status--open', processed:'status--processed', approved:'status--approved', posted:'status--posted', paid:'status--paid' }[status] ?? '';
      },
  
      dataToPrint(details) {
        this.print_content = details.map((d, idx) => ({
          sn:        idx + 1,
          reference: d?.period?.reference,
          name:      d?.period?.name,
          employee:  `${d?.employee?.first_name} ${d?.employee?.last_name}`,
          basic:     d.basic, gross: d.gross, net: d.net, tax: d.tax, pension: d.pension,
          status:    d?.period?.status,
        }));
      },
  
      fetchData(url = '/payroll/payslip') {
        this.loading = true;
        this.tableData.draw++;
        axios.get(url, { params: this.tableData })
          .then(response => {
            const data     = response.data;
            this.details   = data.data.data;
            this.dataToPrint(this.details);
            this.configPagination(data.data);
            this.loading = false;
          })
          .catch(error => {
            this.loading = false;
            if (error?.response?.status == 403) {
              this.permission_status = 403;
              this.toast.error(error.response.data.message);
            }
          });
      },
  
      configPagination(data) {
        this.pagination.lastPage    = data.last_page;
        this.pagination.currentPage = data.current_page;
        this.pagination.total       = data.total;
        this.pagination.lastPageUrl = data.last_page_url;
        this.pagination.nextPageUrl = data.next_page_url;
        this.pagination.prevPageUrl = data.prev_page_url;
        this.pagination.from        = data.from;
        this.pagination.to          = data.to;
      },
  
      formatDate(date) { return moment(date).format('DD/MM/YYYY'); },
  
      formatPrice(value) {
        if (!value && value !== 0) return '0.00';
        return Number(value).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
      },

      openExportModal(format) {
        this.pendingExportFmt   = format;
        this.selectedExportCols = this.exportColumnDefs.map(c => c.key);
        this.showExportModal    = true;
      },

      getPayslipValue(row, key) {
        switch (key) {
          case 'employee':   return row.employee ? `${row.employee.first_name} ${row.employee.last_name}` : '—';
          case 'period':     return row.period?.name || '—';
          case 'basic':      return this.formatPrice(row.basic);
          case 'gross':      return this.formatPrice(row.gross);
          case 'tax':        return this.formatPrice(row.tax);
          case 'pension':    return this.formatPrice(row.pension);
          case 'deductions': return this.formatPrice(row.additional_deduction);
          case 'earnings':   return this.formatPrice(row.additional_earning);
          case 'net':        return this.formatPrice(row.net);
          case 'status':     return this.capitalize(row.period?.status);
          default:           return '—';
        }
      },

      async doExport() {
        if (!this.selectedExportCols.length) return;
        this.exportLoading   = true;
        this.showExportModal = false;
        try {
          const params = { ...this.tableData, page: 1, length: 10000 };
          const response = await axios.get('/payroll/payslip', { params });
          const rows     = response.data.data.data || [];
          const cols     = this.exportColumnDefs.filter(c => this.selectedExportCols.includes(c.key));
          const headers  = cols.map(c => c.label);
          const body     = rows.map(r => cols.map(c => this.getPayslipValue(r, c.key)));
          const date     = new Date().toISOString().slice(0, 10);

          if (this.pendingExportFmt === 'csv') {
            const csv  = [headers, ...body].map(r => r.map(v => `"${String(v).replace(/"/g,'""')}"`).join(',')).join('\n');
            const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8;' });
            const url  = URL.createObjectURL(blob);
            const a    = document.createElement('a');
            a.href = url; a.download = `payslip-history-${date}.csv`; a.click();
            URL.revokeObjectURL(url);
          } else {
            const doc      = new jsPDF({ orientation: 'landscape', unit: 'pt', format: 'a4' });
            const usable   = 761;
            const scale    = usable / cols.reduce((s, c) => s + c.pdfWidth, 0);
            const colStyles = {};
            cols.forEach((c, i) => { colStyles[i] = { cellWidth: c.pdfWidth * scale }; });
            doc.setFontSize(18); doc.setFont('helvetica','bold'); doc.setTextColor(26,43,69);
            doc.text('Payslip History', 40, 46);
            doc.setFontSize(9); doc.setFont('helvetica','normal'); doc.setTextColor(122,144,176);
            doc.text(`Exported ${new Date().toLocaleDateString('en-GB')}   ·   ${rows.length} record(s)`, 40, 62);
            doc.autoTable({
              startY: 78, head: [headers], body,
              styles: { fontSize:7.5, cellPadding:3.5, textColor:[26,43,69], overflow:'ellipsize' },
              headStyles: { fillColor:[0,74,173], textColor:255, fontStyle:'bold', fontSize:7.5 },
              alternateRowStyles: { fillColor:[248,250,252] },
              columnStyles: colStyles, tableWidth: usable, margin: { left:40, right:40 },
              didDrawPage(d) {
                doc.setFontSize(8); doc.setTextColor(192,202,214);
                doc.text(`Page ${d.pageNumber}`, doc.internal.pageSize.width/2, doc.internal.pageSize.height-16, { align:'center' });
              },
            });
            doc.save(`payslip-history-${date}.pdf`);
          }
        } catch {
          this.toast.error('Export failed. Please try again.');
        } finally {
          this.exportLoading = false;
        }
      },
    },
  };
  </script>
  
  <style scoped>
  .page-header  { display:flex; align-items:flex-start; justify-content:space-between; flex-wrap:wrap; gap:14px; margin-bottom:20px; }
  .page-title   { font-size:20px; font-weight:700; color:#1a2b45; margin:0 0 3px; }
  .page-sub     { font-size:13px; color:#7a90b0; margin:0; }
  .page-actions { display:flex; align-items:center; flex-wrap:wrap; gap:8px; }
  
  .search-wrap  { position:relative; display:flex; align-items:center; }
  .search-icon  { position:absolute; left:11px; color:#7a90b0; font-size:13px; pointer-events:none; }
  .search-input { padding:8px 12px 8px 32px; border:1px solid #dde5f0; border-radius:8px; font-size:13px; color:#1a2b45; background:#fff; width:200px; outline:none; transition:border-color .15s; }
  .search-input:focus { border-color:#004AAD; }
  .search-input::placeholder { color:#aab8cc; }
  
  .btn-outline { display:inline-flex; align-items:center; gap:7px; background:#fff; color:#1a2b45; border:1px solid #dde5f0; font-size:13px; font-weight:500; padding:8px 14px; border-radius:8px; cursor:pointer; transition:background .15s; white-space:nowrap; }
  .btn-outline:hover { background:#f4f7fb; }
  .btn-outline::after { display:none !important; }
  .me-2 { margin-right:8px; } .mt-3 { margin-top:12px; }
  
  /* ── Data Card ── */
  .data-card { background:#fff; border:1px solid #e4eaf2; border-radius:12px; overflow:hidden; }
  
  .state-block  { display:flex; flex-direction:column; align-items:center; justify-content:center; padding:64px 24px; text-align:center; }
  .state-icon   { width:60px; height:60px; border-radius:16px; display:flex; align-items:center; justify-content:center; font-size:24px; margin-bottom:16px; }
  .state-icon--blue { background:rgba(0,74,173,.1); color:#004AAD; }
  .state-icon--red  { background:rgba(224,53,53,.1); color:#e03535; }
  .state-title  { font-size:16px; font-weight:700; color:#1a2b45; margin:0 0 6px; }
  .state-sub    { font-size:13px; color:#7a90b0; margin:0; }
  
  /* ── Toolbar ── */
  .table-toolbar { display:flex; align-items:center; justify-content:space-between; padding:14px 20px; border-bottom:1px solid #e4eaf2; flex-wrap:wrap; gap:10px; }
  .table-count   { font-size:13px; color:#7a90b0; }
  .toolbar-right { display:flex; align-items:center; gap:8px; }
  .rows-select   { border:1px solid #dde5f0; border-radius:7px; padding:5px 10px; font-size:13px; color:#1a2b45; background:#f8fafc; outline:none; cursor:pointer; }
  
  /* ── Table ── */
  .table-wrap  { overflow-x:auto; }
  .data-table  { width:100%; border-collapse:collapse; }
  .data-table thead tr { background:#f8fafc; }
  .data-table thead th { font-size:11.5px; font-weight:700; text-transform:uppercase; letter-spacing:.05em; color:#7a90b0; padding:12px 14px; border-bottom:1px solid #e4eaf2; white-space:nowrap; }
  .data-table tbody tr { border-bottom:1px solid #f0f4f8; transition:background .12s; }
  .data-table tbody tr:last-child { border-bottom:none; }
  .data-table tbody tr:hover { background:#f8fafc; }
  .data-table tbody td { padding:12px 14px; font-size:13px; vertical-align:middle; }
  .col-actions { width:48px; text-align:center; }
  .text-right      { text-align:right; }
  .text-muted-cell { color:#4a5e78; }
  .fw-bold         { font-weight:700; }
  .amount-value    { color:#004AAD; }
  .deduction-value { color:#e03535; font-weight:600; }
  .earning-value   { color:#16a34a; font-weight:600; }
  
  /* ── Employee Cell ── */
  .employee-cell   { display:flex; align-items:center; gap:10px; }
  .employee-avatar { width:34px; height:34px; border-radius:50%; background:rgba(0,74,173,.1); color:#004AAD; display:flex; align-items:center; justify-content:center; font-size:12px; font-weight:700; flex-shrink:0; }
  .employee-name   { font-size:13.5px; font-weight:600; color:#1a2b45; margin:0 0 2px; }
  .employee-sub    { font-size:11px; color:#7a90b0; margin:0; }
  
  /* ── Period Cell ── */
  .period-name { font-size:13px; font-weight:600; color:#1a2b45; margin:0 0 4px; }
  .batch-code  { font-family:monospace; font-size:11.5px; font-weight:600; color:#7a90b0; background:#f4f7fb; border:1px solid #e4eaf2; border-radius:5px; padding:1px 6px; }
  
  /* ── Status Badges ── */
  .status-badge      { display:inline-block; border-radius:6px; padding:3px 10px; font-size:12px; font-weight:600; border:1px solid transparent; white-space:nowrap; }
  .status--open      { background:#fff7ed; color:#e08c00; border-color:#fed7aa; }
  .status--processed { background:#f0fdf4; color:#16a34a; border-color:#bbf7d0; }
  .status--approved  { background:#edf4ff; color:#004AAD; border-color:#c5d6f5; }
  .status--posted    { background:#f5f3ff; color:#7c3aed; border-color:#c4b5fd; }
  .status--paid      { background:#f0fdf4; color:#0d9488; border-color:#99f6e4; }
  
  /* ── Action Button ── */
  .action-btn  { width:30px; height:30px; border-radius:7px; border:1px solid #e4eaf2; display:flex; align-items:center; justify-content:center; font-size:13px; cursor:pointer; transition:background .15s; text-decoration:none; background:#fff; margin:0 auto; }
  .action-btn.disabled { opacity:.4; pointer-events:none; }
  .action-btn--view { color:#0d9488; }
  .action-btn--view:hover:not(.disabled) { background:#f0fdfb; border-color:#a7f3d0; text-decoration:none; }
  
  /* ── Footer ── */
  .table-footer { padding:14px 20px; border-top:1px solid #e4eaf2; display:flex; justify-content:flex-end; }
  
  /* ── Responsive ── */
  @media(max-width:767px) {
    .page-header,.page-actions { flex-direction:column; align-items:stretch; }
    .search-input { width:100%; }
  }

  .btn-export-sm { display:inline-flex; align-items:center; gap:5px; background:#fff; color:#16a34a; border:1px solid #bbf7d0; font-size:12px; font-weight:600; padding:8px 12px; border-radius:8px; cursor:pointer; font-family:inherit; white-space:nowrap; }
  .btn-export-sm:hover:not(:disabled) { background:#f0fdf4; }
  .btn-export-sm--pdf { color:#dc2626; border-color:#fecaca; }
  .btn-export-sm--pdf:hover:not(:disabled) { background:#fff5f5; }
  .btn-export-sm:disabled { opacity:.45; cursor:not-allowed; }

  /* -- Column picker modal ------------------------------------------------- */
  .col-picker-backdrop { position:fixed; inset:0; background:rgba(15,25,40,.45); z-index:1050; display:flex; align-items:center; justify-content:center; padding:20px; }
  .col-picker-modal    { background:#fff; border-radius:14px; width:100%; max-width:480px; box-shadow:0 24px 60px rgba(0,0,0,.18); overflow:hidden; }
  .col-picker-header   { display:flex; align-items:flex-start; justify-content:space-between; padding:20px 22px 14px; border-bottom:1px solid #f0f4f9; }
  .col-picker-title    { font-size:15px; font-weight:700; color:#1a2b45; margin:0 0 3px; }
  .col-picker-sub      { font-size:12px; color:#7a90b0; margin:0; }
  .col-picker-close    { background:none; border:none; color:#7a90b0; font-size:15px; cursor:pointer; padding:4px; line-height:1; }
  .col-picker-close:hover { color:#1a2b45; }
  .col-picker-actions  { display:flex; align-items:center; gap:8px; padding:10px 22px; background:#f8fafc; font-size:12px; border-bottom:1px solid #f0f4f9; }
  .col-picker-link     { background:none; border:none; color:#004AAD; font-size:12px; cursor:pointer; padding:0; font-weight:600; font-family:inherit; }
  .col-picker-link:hover { text-decoration:underline; }
  .col-picker-sep      { color:#c5d3e0; }
  .col-picker-count    { margin-left:auto; color:#7a90b0; font-size:11.5px; }
  .col-picker-grid     { display:grid; grid-template-columns:1fr 1fr; gap:2px; padding:14px 16px; max-height:280px; overflow-y:auto; }
  .col-picker-item     { display:flex; align-items:center; gap:9px; padding:9px 10px; border-radius:8px; cursor:pointer; transition:background .1s; }
  .col-picker-item:hover { background:#f4f7fb; }
  .col-picker-check    { width:15px; height:15px; accent-color:#004AAD; cursor:pointer; flex-shrink:0; }
  .col-picker-label    { font-size:13px; color:#1a2b45; user-select:none; }
  .col-picker-footer   { display:flex; justify-content:flex-end; gap:8px; padding:14px 20px; border-top:1px solid #f0f4f9; }
  .col-picker-btn-ghost   { display:inline-flex; align-items:center; gap:7px; background:#f4f7fb; color:#3d5068; font-size:13px; font-weight:600; padding:8px 14px; border-radius:8px; border:1px solid #dde5f0; cursor:pointer; font-family:inherit; }
  .col-picker-btn-ghost:hover { background:#e4eaf2; }
  .col-picker-btn-primary { display:inline-flex; align-items:center; gap:7px; background:#004AAD; color:#fff; font-size:13px; font-weight:600; padding:9px 18px; border-radius:8px; border:none; cursor:pointer; font-family:inherit; transition:background .15s; }
  .col-picker-btn-primary:hover:not(:disabled) { background:#003b8e; }
  .col-picker-btn-primary:disabled { opacity:.45; cursor:not-allowed; }
  .fade-enter-active, .fade-leave-active { transition:opacity .15s ease; }
  .fade-enter, .fade-leave-to { opacity:0; }
  </style>